/**
 * Narrow analytics access in «Настройки → Команда» — what the screen may offer
 * and how it names what the server answered (E1.5 PR 5b, #1336; spec
 * `docs/plans/2026-10-01-dashboard-e15-access-grants.md`, «PR 5 — дизайн»).
 *
 * The server judges every write again. These helpers only stop the screen from
 * offering an action that would be refused, with the SAME rule: the RBAC action
 * `user.manage_access` (`checkPermission`, `@aica/core/rbac`) plus the route's
 * own two — never oneself, and a grant only on a `supervisor`/`agent` (owner
 * decision 05.10).
 */
import { ACCESS_GRANTS_FLAG_KEY, findTierFeatureGate } from '@aica/core';
import { PRESET_CAPABILITIES, checkPermission, toGlobalRole } from '@aica/core/rbac';
import {
  ACCESS_GRANT_ERROR_STATUS,
  GRANT_BLOCKS_ROLE_ERROR_CODE,
  isAccessGrantBaseRole,
  type AccessDepartment,
  type AccessGrantAssignment,
  type AccessGrantEntry,
  type AccessGrantErrorCode,
  type AccessGrantView,
  type AccessPreset,
  type AccessScope,
} from '@aica/api-types/schemas';
import { TIER_DISPLAY } from '@aica/stripe/tier-config';
import { ApiError } from '@/lib/fetch-utils';

const accessGrantsGate = findTierFeatureGate(ACCESS_GRANTS_FLAG_KEY);

/**
 * The plan narrow access starts at, by the catalog's floor — from `TIER_DISPLAY`,
 * the one source the API's own refusal text takes it from. Empty when the catalog
 * has no tier gate for the feature: the server then never answers
 * `tier_not_eligible`, and the name is not shown.
 */
export const ACCESS_GRANTS_PLAN_NAME = accessGrantsGate
  ? TIER_DISPLAY[accessGrantsGate.minTier].name
  : '';

/** The viewer as the screen knows them. */
export interface AccessViewer {
  /** The viewer's Clerk id (`useRole().userId`) — what `clerkUserId` of a list row is compared with. */
  clerkUserId: string | null;
  globalRole: string | null;
  isOwner: boolean;
  orgId: string | null;
}

/** The fields of a Team row these rules read. */
export interface AccessMember {
  id: string;
  role: string;
  isOwner?: boolean;
  clerkUserId?: string;
}

/**
 * What the «Доступ» button of a row does:
 * - `assign` — give, replace or revoke (a `supervisor`/`agent`, while
 *   assigning is open);
 * - `revoke` — only take away: a `client`/`admin` row that holds a grant
 *   (it can only exist around the API, and revoking is how it is repaired),
 *   or any row that holds one while assigning is closed (below Pro or the
 *   flag off — a grant it already holds stays enforced and must stay
 *   removable);
 * - `null` — no button.
 */
export type MemberAccessMode = 'assign' | 'revoke';

function viewerMayManage(viewer: AccessViewer, targetRole: string): boolean {
  if (!viewer.orgId) return false;
  const role = toGlobalRole(targetRole);
  if (!role) return false;
  return checkPermission(
    {
      userId: viewer.clerkUserId ?? '',
      clerkUserId: viewer.clerkUserId ?? '',
      orgId: viewer.orgId,
      globalRole: viewer.globalRole ? toGlobalRole(viewer.globalRole) : null,
      isOwner: viewer.isOwner,
      projectRoles: new Map(),
    },
    'manage_access',
    { type: 'user', orgId: viewer.orgId, targetUserOrgId: viewer.orgId, targetRole: role }
  );
}

/**
 * May this viewer manage access at all? Gates the list read and the whole
 * column: the roles of `MANAGE_ACCESS_ROLES` and the platform owner. Asked
 * about a base-role target, which every manager may touch.
 */
export function viewerCanManageAccess(viewer: AccessViewer): boolean {
  return viewerMayManage(viewer, 'agent');
}

export function memberAccessMode(
  viewer: AccessViewer,
  member: AccessMember,
  grant: AccessGrantEntry | undefined,
  /** `enableAccessGrants` resolves on (tier and flag): the server accepts assignments. */
  assigningOpen = true
): MemberAccessMode | null {
  // The platform owner is always `legacy` — a grant on them means nothing.
  if (member.isOwner) return null;
  if (viewer.clerkUserId && member.clerkUserId === viewer.clerkUserId) return null;
  if (!viewerMayManage(viewer, member.role)) return null;
  if (assigningOpen && isAccessGrantBaseRole(member.role)) return 'assign';
  return grant ? 'revoke' : null;
}

/** What the administrator has chosen in the preset/scope picker. */
export interface AccessPick {
  preset: AccessPreset;
  /** «Аналитик»: the whole org or chosen projects. «Руководитель группы» ignores it. */
  scopeKind: 'org' | 'projects';
  projectIds: string[];
  departmentIds: string[];
}

export const EMPTY_ACCESS_PICK: AccessPick = {
  preset: 'analyst',
  scopeKind: 'org',
  projectIds: [],
  departmentIds: [],
};

/**
 * The request body for a pick — the pair the server's
 * `accessGrantAssignmentSchema` accepts — or null while nothing is chosen
 * (projects or departments with an empty list).
 */
export function accessAssignmentOf(pick: AccessPick): AccessGrantAssignment | null {
  if (pick.preset === 'group_lead') {
    return pick.departmentIds.length > 0
      ? { preset: 'group_lead', scope: { kind: 'departments', ids: pick.departmentIds } }
      : null;
  }
  if (pick.scopeKind === 'org') return { preset: 'analyst', scope: { kind: 'org' } };
  return pick.projectIds.length > 0
    ? { preset: 'analyst', scope: { kind: 'projects', ids: pick.projectIds } }
    : null;
}

/**
 * The grant a just-sent invite with access creates, as the grants list will
 * name it once it is read again: the screen draws it meanwhile instead of
 * claiming «по роли» (and «no projects») for a person who was invited with
 * exactly that grant. The id is the invited person's — the server's grant id
 * and time are not in the invite answer and nothing here reads them; the
 * re-read replaces this entry.
 */
export function optimisticGrantOf(
  userId: string,
  assignment: AccessGrantAssignment,
  now: Date = new Date()
): AccessGrantView {
  return {
    state: 'active',
    id: `invited:${userId}`,
    userId,
    preset: assignment.preset,
    capabilities: [...PRESET_CAPABILITIES[assignment.preset]],
    scope: assignment.scope,
    grantedBy: null,
    grantedAt: now.toISOString(),
  };
}

export const DEPARTMENT_PATH_SEPARATOR = ' › ';

/**
 * The org's departments in TREE order (a department right after its parent,
 * siblings by name) with the label each is shown under: the names from the
 * root down, «Киев › Отдел продаж». Two departments may share a name, and a
 * grant on a group lead covers the whole subtree — the bare name would not
 * say which one, or how much, is chosen. A parent the list does not carry
 * makes its child a root; a cycle (which the structure never produces) is cut
 * rather than looped over.
 */
export interface DepartmentTreeEntry {
  id: string;
  name: string;
  /** The names from the root down, joined by {@link DEPARTMENT_PATH_SEPARATOR}. */
  label: string;
}

export function departmentTreeOf(
  departments: ReadonlyArray<AccessDepartment>
): DepartmentTreeEntry[] {
  const ids = new Set(departments.map((department) => department.id));
  const byName = (a: AccessDepartment, b: AccessDepartment) =>
    a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  const children = new Map<string | null, AccessDepartment[]>();
  for (const department of departments) {
    const parent =
      department.parentId !== null && ids.has(department.parentId)
        ? department.parentId
        : null;
    const siblings = children.get(parent);
    if (siblings) siblings.push(department);
    else children.set(parent, [department]);
  }
  const ordered: DepartmentTreeEntry[] = [];
  const seen = new Set<string>();
  const visit = (parentId: string | null, trail: readonly string[]) => {
    // The server lists the tree by `path` (ids), so siblings are not in name
    // order: they are sorted here, once per parent, in the array built above.
    for (const department of children.get(parentId)?.sort(byName) ?? []) {
      if (seen.has(department.id)) continue;
      seen.add(department.id);
      const names = [...trail, department.name];
      ordered.push({
        id: department.id,
        name: department.name,
        label: names.join(DEPARTMENT_PATH_SEPARATOR),
      });
      visit(department.id, names);
    }
  };
  visit(null, []);
  // Only a cycle leaves anything unvisited: show those under their own names.
  if (ordered.length < departments.length) {
    for (const department of [...departments].sort(byName)) {
      if (!seen.has(department.id)) {
        ordered.push({
          id: department.id,
          name: department.name,
          label: department.name,
        });
      }
    }
  }
  return ordered;
}

/** The names `scopeSummary` needs: id → label of the org's projects and departments. */
export interface ScopeNames {
  projects: ReadonlyMap<string, string>;
  departments: ReadonlyMap<string, string>;
}

export type ScopeSummary =
  | { kind: 'org' }
  | { kind: 'projects'; names: string[]; unnamed: number }
  | { kind: 'departments'; names: string[]; unnamed: number };

/** A scope as names: ids the screen has no name for are counted, not shown raw. */
export function scopeSummary(scope: AccessScope, names: ScopeNames): ScopeSummary {
  if (scope.kind === 'org') return { kind: 'org' };
  const source = scope.kind === 'projects' ? names.projects : names.departments;
  const known = scope.ids.flatMap((id) => {
    const name = source.get(id);
    return name === undefined ? [] : [name];
  });
  return { kind: scope.kind, names: known, unnamed: scope.ids.length - known.length };
}

/**
 * What a failed write or read is, for the message the dialog shows. The
 * server's codes come from `AccessGrantErrorCode` (api-types): a code added
 * there is a type error here and in the message tables until it is handled,
 * not a silent «unknown».
 */
export type AccessGrantFailure =
  | AccessGrantErrorCode
  | 'feature_disabled'
  | 'tier_not_eligible'
  | 'policy_unavailable'
  | 'forbidden'
  | 'not_found'
  | 'unknown';

const BODY_CODES: ReadonlySet<string> = new Set<AccessGrantFailure>([
  ...(Object.keys(ACCESS_GRANT_ERROR_STATUS) as AccessGrantErrorCode[]),
  'feature_disabled',
  'tier_not_eligible',
]);

/**
 * Tell the answers apart by their body code. Only a CODE makes an answer an
 * access one: 409 is «someone changed it — reload», 422 is «this cannot be
 * given», 503 `access_policy_unavailable` is «could not be read, try again».
 * A bare 503 is infrastructure and a bare 403 on a shared route (the invite)
 * is the plain invite right, so neither is claimed here.
 *
 * `member` routes (`/users/:id/access`) are the exception for the status: an
 * uncoded 403 there is the role (A23) and an uncoded 404 a person who left.
 * The invite shares its route with the plain invite, which answers the same
 * statuses for other reasons — it asks for `invite`, where only codes count.
 */
export function accessGrantFailure(
  error: unknown,
  route: 'member' | 'invite' = 'member'
): AccessGrantFailure {
  if (!(error instanceof ApiError)) return 'unknown';
  const code = (error.data as { code?: unknown } | null | undefined)?.code;
  if (typeof code === 'string' && BODY_CODES.has(code)) return code as AccessGrantFailure;
  if (code === 'access_policy_unavailable') return 'policy_unavailable';
  if (route === 'member') {
    if (error.status === 403) return 'forbidden';
    if (error.status === 404) return 'not_found';
  }
  return 'unknown';
}

/** 409 `grant_blocks_role` of `PATCH /users/:id/role`: the person holds a grant. */
export function isGrantBlocksRole(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    (error.data as { error?: unknown } | null | undefined)?.error ===
      GRANT_BLOCKS_ROLE_ERROR_CODE
  );
}
